import React, { useState } from 'react';
import type { PackageOffer, PackageOfferStatus, PackageOfferUnit, Guide } from '../../types';
import PackageDetailsModal from './PackageDetailsModal';

const OFFER_STATUS_LABELS: Record<PackageOfferStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn'
};

const OFFER_STATUS_CLASSES: Record<PackageOfferStatus, string> = {
  draft: 'bg-[#E8DFBD] text-[#2C2731]',
  sent: 'bg-[#E8DFBD] text-[#2C2731]',
  accepted: 'bg-[#C4E6D2] text-[#1B4732]',
  rejected: 'bg-[#F5D2D2] text-[#7A1C1C]',
  withdrawn: 'bg-[#F0E8CC] text-[#625C66]'
};

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation Session',
  preparation: 'Preparation Session',
  integration: 'Integration Session',
  journey: 'Journey Session',
  ceremony: 'Ceremony',
  follow_up: 'Follow-up Session',
  additional: 'Additional Support'
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatDate = (value: string) => {
  try {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  } catch {
    // fall through to raw value
  }
  return value;
};

type UnitGroup = {
  id: string;
  componentType: string;
  componentName: string;
  label: string;
  quantity: number;
  scheduled: number;
  fulfilled: number;
  cancelled: number;
  priceCents: number;
};

const buildUnitLabel = (unit: PackageOfferUnit) => {
  const typeKey = unit.componentType.toLowerCase();
  const baseLabel = COMPONENT_TYPE_LABELS[typeKey] ?? unit.componentType;
  if (unit.componentName && unit.componentName.toLowerCase() !== baseLabel.toLowerCase()) {
    return baseLabel;
  }
  return baseLabel;
};

const groupUnits = (units: PackageOfferUnit[]): UnitGroup[] => {
  const groups = new Map<string, UnitGroup>();

  units.forEach((unit) => {
    const key = `${unit.componentType}|${unit.componentName}`;
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += 1;
      existing.priceCents += unit.unitPriceCents;
      if (unit.status === 'scheduled') existing.scheduled += 1;
      if (unit.status === 'fulfilled') existing.fulfilled += 1;
      if (unit.status === 'cancelled') existing.cancelled += 1;
      return;
    }

    groups.set(key, {
      id: key,
      componentType: unit.componentType,
      componentName: unit.componentName,
      label: buildUnitLabel(unit),
      quantity: 1,
      scheduled: unit.status === 'scheduled' ? 1 : 0,
      fulfilled: unit.status === 'fulfilled' ? 1 : 0,
      cancelled: unit.status === 'cancelled' ? 1 : 0,
      priceCents: unit.unitPriceCents
    });
  });

  return Array.from(groups.values()).sort((a, b) => {
    const typeCompare = a.componentType.localeCompare(b.componentType);
    if (typeCompare !== 0) return typeCompare;
    return a.componentName.localeCompare(b.componentName);
  });
};

type PackageOfferCardProps = {
  offer: PackageOffer;
  guide?: Guide | null;
  onPurchase?: () => void;
  isPurchasing?: boolean;
};

export const PackageOfferCard: React.FC<PackageOfferCardProps> = ({ offer, guide = null, onPurchase, isPurchasing = false }) => {
  const [showDetails, setShowDetails] = useState(false);
  const unitGroups = groupUnits(offer.units);
  const totalUnits = offer.units.length;
  const formattedTotal = formatCurrency(offer.totalCents / 100);
  const sentDate = formatDate(offer.createdAt);
  const statusLabel = OFFER_STATUS_LABELS[offer.status];
  const statusBadgeClass = OFFER_STATUS_CLASSES[offer.status];

  return (
    <>
      <section className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/70">Your Package</p>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-[#2C2731]">{offer.name}</h3>
              <p className="text-sm text-[#2C2731]/80 line-clamp-3">
                {offer.description || "Review the details below and purchase when you're ready."}
              </p>
              <p className="text-xs text-[#2C2731]/60">
                {sentDate ? `Sent ${sentDate}` : 'Recently shared with you'}
                {offer.durationMonths ? ` • ${offer.durationMonths} month${offer.durationMonths === 1 ? '' : 's'}` : ''}
              </p>
            </div>
            {statusLabel && (
              <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass}`}>
                {statusLabel}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowDetails(true)}
            className="flex shrink-0 items-center gap-2 rounded-lg border border-[#2C2731] px-3 py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
          >
            <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.556 0 8.25-3.694 8.25-8.25S16.556 3.75 12 3.75 3.75 7.444 3.75 12 7.444 20.25 12 20.25z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v.75m0 3v3" />
            </svg>
            View Details
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-[#2C2731]/40 bg-[#E8DFBD] px-3 py-3">
          <div className="flex flex-col">
            <span className="text-xs font-medium uppercase tracking-wide text-[#2C2731]/70">Total Package Value</span>
            <span className="text-xl font-semibold text-[#2C2731]">{formattedTotal}</span>
          </div>
          <div className="text-right text-sm text-[#2C2731]/70">
            <span>{totalUnits} item{totalUnits === 1 ? '' : 's'}</span>
            {offer.durationMonths ? (
              <p className="text-xs text-[#2C2731]/60">{offer.durationMonths} month plan</p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {unitGroups.map((group) => {
            const statusPieces: string[] = [];
            if (group.fulfilled > 0) {
              statusPieces.push(`${group.fulfilled} complete`);
            }
            if (group.scheduled > 0) {
              statusPieces.push(`${group.scheduled} scheduled`);
            }
            if (group.cancelled > 0) {
              statusPieces.push(`${group.cancelled} cancelled`);
            }
            const unitStatusLabel = statusPieces.join(' • ');
            const labelWithQuantity =
              group.quantity > 1 ? `${group.quantity} | ${group.label}${group.label.toLowerCase().endsWith('s') ? '' : 's'}` : `${group.quantity} | ${group.label}`;

            return (
              <div key={group.id} className="rounded-xl border border-[#2C2731]/40 bg-[#FFFCEF] px-3 py-3 shadow-sm">
                <p className="text-sm font-semibold text-[#2C2731]">{labelWithQuantity}</p>
                {group.componentName && (
                  <p className="text-xs text-[#2C2731]/60">{group.componentName}</p>
                )}
                <div className="mt-2 flex items-center justify-between text-xs text-[#2C2731]/70">
                  <span>{formatCurrency(group.priceCents / 100)}</span>
                  {unitStatusLabel && <span>{unitStatusLabel}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onPurchase}
          disabled={!onPurchase || isPurchasing}
          className={`mt-5 w-full rounded-lg py-3 text-sm font-semibold transition ${
            onPurchase && !isPurchasing
              ? 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#1F1924] active:bg-[#160F1B]'
              : 'cursor-not-allowed bg-[#E8DFBD] text-[#2C2731]/50'
          }`}
        >
          {isPurchasing ? 'Processing...' : 'Purchase Now'}
        </button>

        <p className="mt-4 text-xs text-[#2C2731]/60">Have questions? Message your guide if you need any changes before purchasing.</p>
      </section>

      {showDetails && (
        <PackageDetailsModal
          offer={offer}
          guide={guide ?? undefined}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
};

export default PackageOfferCard;
